import { definedMap, withDefault } from "collection-utils";
import { DateFormat, formatDateTime, TimeFormat } from "../../date-time/format-date-time";
import { GlideDateTime } from "../../date-time/glide-date-time";
import { parseUserDateTimeZoneAgnostic, parseUserDateTimeZoneAgnosticSync } from "../../date-time/parse-user-date-time";
import { convertDateToTimeZoneAgnostic, makeDateFromString } from "../../date-time/support";
import { logError, logInfo, logTrace } from "../debug-print";
import type { WebhookValue } from "../firebase-function-types";
import { formatNumber } from "../format-number";
import { assert, defined, isArray, panic, parseNumber } from "../Support";
import type {
    ActionSource,
    BasePrimitiveValue,
    EnvironmentBoundEvaluable,
    EvalEnvironment,
    Evaluable,
    ForceValueProducer,
    Loading,
    ObjectInspector,
    ObjectValue,
    PotentialValue,
    RootEvalEnvironment,
    Value,
    ValueListener,
    ValueProducer,
} from "./computation-types";
import { Callable, isBasePrimitiveValue, isObjectValue, LOADING } from "./computation-types";

export function loadedMap<T, U>(v: T | Loading, f: (v: T) => U): U | Loading {
    if (v === LOADING) return LOADING;
    return f(v);
}

export function loadedMapWithDefault<T, U>(v: T | Loading, defaultValue: U, f: (v: T) => U): U {
    if (v === LOADING) return defaultValue;
    return f(v);
}

export function isLoadedDefined<T>(v: T | Loading | undefined): v is T {
    return v !== LOADING && v !== undefined;
}

export function loadedDefinedMap<T, U>(v: T | Loading | undefined, f: (v: T) => U): U | undefined {
    if (isLoadedDefined(v)) {
        return f(v);
    }
    return undefined;
}

export function loadedDefinedMapWithDefault<T, U>(v: T | Loading | undefined, defaultValue: U, f: (v: T) => U): U {
    if (isLoadedDefined(v)) {
        return f(v);
    }
    return defaultValue;
}

export function nonLoading<T>(v: T | Loading): T {
    if (v === LOADING) {
        return panic("Value is loading but shouldn't be");
    }
    return v;
}

let didDetectRecursiveComputations: boolean = false;

export function getAndResetRecursiveComputationsDetected(): boolean {
    const result = didDetectRecursiveComputations;
    didDetectRecursiveComputations = false;
    return result;
}

export function detectedRecursiveComputation(): undefined {
    if (!didDetectRecursiveComputations) {
        logTrace("Seems like we're doing a recursive computation - aborting");
        didDetectRecursiveComputations = true;
    }
    return undefined;
}

export class ChainedEvalEnvironment<O, A> implements EvalEnvironment<O, A> {
    constructor(
        private readonly _bindings: ReadonlyMap<string, EnvironmentBoundEvaluable<O, A>>,
        private readonly _next: EvalEnvironment<O, A>
    ) {}

    public lookupUserDefinedComponent(name: string): Evaluable<O, A> | undefined {
        return this._next.lookupUserDefinedComponent(name);
    }

    public get root(): RootEvalEnvironment<O, A> {
        return this._next.root;
    }

    public get(name: string): EnvironmentBoundEvaluable<O, A> | undefined {
        const result = this._bindings.get(name);
        if (result !== undefined) return result;
        return this._next.get(name);
    }

    public get actionSource(): ActionSource | undefined {
        return this._next.actionSource;
    }
}

// let totalSubscribers = 0;
// const subscriptions: [ValueProducer, ValueListener][] = [];

// function checkTotalSubscribers(): void {
//     if (totalSubscribers % 10 === 0) {
//         logInfo("total subscribers", totalSubscribers);
//     }
// }

// function printSubscription(event: string, producer: any, listener: any): void {
//     logInfo(`${event} producer ${producer.id} listener ${listener.id}`);
// }

// function addSubscription(producer: ValueProducer, listener: ValueListener): void {
//     totalSubscribers++;
//     printSubscription("subscribed", producer, listener);
//     subscriptions.push([producer, listener]);
//     checkTotalSubscribers();
// }

// const producersByName = new DefaultMap<string, number>(() => 0);
// const listenersByName = new DefaultMap<string, number>(() => 0);
// const uniqueProducers = new Set<any>();
// const uniqueListeners = new Set<any>();

// function addSubscription<O, A>(producer: ValueProducer<O, A>, listener: ValueListener<O, A>): void {
//     producersByName.update(producer.constructor.name, x => x + 1);
//     listenersByName.update(listener.constructor.name, x => x + 1);
//     uniqueProducers.add(producer);
//     uniqueListeners.add(listener);
// }

// function removeSubscription(producer: ValueProducer, listener: ValueListener): void {
//     totalSubscribers--;
//     printSubscription("unsubscribed", producer, listener);
//     for (let i = 0; i < subscriptions.length; i++) {
//         const [p, l] = subscriptions[i];
//         if (p !== producer || l !== listener) continue;
//         const x = defined(subscriptions.pop());
//         if (i < subscriptions.length) {
//             subscriptions[i] = x;
//         }
//         checkTotalSubscribers();
//         return;
//     }
//     logError("removeSubscription didn't find subscription", producer, listener);
// }

// export function printSubscriptions(): void {
//     logInfo("subscriptions");
//     for (const [p, l] of subscriptions) {
//         if ((l as any)._listeners === undefined) {
//             logInfo("producer", p, "listener", l);
//         }
//     }
// }

const printUpdates = false;
let listenersAdded = 0;
let listenersRemoved = 0;
let listenersRemoveIterations = 0;
let maxListeners = 0;
let updateListenersCalled = 0;
let listenersUpdated = 0;
// const listenersUpdatedSet: Set<ValueListener> = new Set();

let resetScheduled = false;

function printAndResetListenerCounts(): void {
    if (printUpdates) {
        logInfo("listenersAdded", listenersAdded);
        logInfo("listenersRemoved", listenersRemoved);
        logInfo("listenersRemoveIterations", listenersRemoveIterations);
        logInfo("maxListeners", maxListeners);
        logInfo("updateListenersCalled", updateListenersCalled);
        logInfo("listenersUpdated", listenersUpdated);
        // logInfo("unique listeners", listenersUpdatedSet.size);

        // logInfo("unique producers", uniqueProducers.size);
        // logInfo("unique listeners", uniqueListeners.size);
        // logInfo("producers by name", Array.from(producersByName.entries()));
        // logInfo("listeners by name", Array.from(listenersByName.entries()));
    }

    listenersAdded = 0;
    listenersRemoved = 0;
    listenersRemoveIterations = 0;
    maxListeners = 0;
    updateListenersCalled = 0;
    listenersUpdated = 0;
    // listenersUpdatedSet.clear();

    // uniqueProducers.clear();
    // uniqueListeners.clear();
    // producersByName.clear();
    // listenersByName.clear();

    resetScheduled = false;
}

// if (printUpdates) {
//     setInterval(printAndResetListenerCounts, 1000);
// }

type FastSet<T> = T | Set<T> | undefined;

function fastSetIsEmpty<T>(set: FastSet<T>): boolean {
    if (set === undefined) return true;
    if (set instanceof Set) return set.size === 0;
    return false;
}

function fastSetAdd<T>(set: FastSet<T>, x: T): FastSet<T> {
    if (set === undefined) return x;
    if (!(set instanceof Set)) {
        if (set === x) return set;

        const s = new Set<T>();
        s.add(set);
        set = s;
    }
    set.add(x);
    return set;
}

function fastSetDelete<T>(set: FastSet<T>, x: T): FastSet<T> {
    if (set === undefined) return x;
    if (set instanceof Set) {
        set.delete(x);
        // FIXME: Return `undefined` if empty?
        return set;
    }
    if (set === x) return undefined;
    return set;
}

function fastSetClear<T>(set: FastSet<T>): FastSet<T> {
    if (set instanceof Set) {
        set.clear();
        return set;
    }
    return undefined;
}

function fastSetHas<T>(set: FastSet<T>, x: T): boolean {
    if (set === undefined) return false;
    if (set instanceof Set) return set.has(x);
    return set === x;
}

// const currentlyUpdating: Set<SubscribableValueProducer> = new Set();

export abstract class SubscribableValueProducer<O, A> implements ValueProducer<O, A> {
    // public readonly id: number;

    private _listeners: FastSet<ValueListener<O, A>>;

    public abstract get current(): PotentialValue<O, A>;

    protected setListenersDirty(): void {
        // assert(!currentlyUpdating.has(this));
        // try {
        //     currentlyUpdating.add(this);

        if (!resetScheduled) {
            assert(updateListenersCalled === 0);
            setTimeout(printAndResetListenerCounts, 0);
            resetScheduled = true;
        }

        updateListenersCalled++;

        if (this._listeners === undefined) return;

        if (this._listeners instanceof Set) {
            // This used to be `Array.from(this._listeners)` because we
            // had to handle the case where setting dirty would cause
            // recomputation, and thereby mutating the listeners set.
            // This shouldn't happen anymore.  If it does, that's a bug.
            for (const l of this._listeners) {
                listenersUpdated++;
                // listenersUpdatedSet.add(l);
                l.setDirty(this);
            }
        } else {
            listenersUpdated++;
            this._listeners.setDirty(this);
        }

        // } finally {
        //     currentlyUpdating.delete(this);
        // }
    }

    public abstract set(v: Value<O, A>): boolean;
    public abstract addProperty(name: string, vp: ValueProducer<O, A>): void;
    public abstract removeProperty(name: string): void;
    public abstract removeFromParent(): void;
    public abstract appendItem(vp: ValueProducer<O, A>): void;
    public abstract removeLastItem(): void;
    public abstract removeItemAtIndex(index: number): void;
    public abstract removeItem(item: ValueProducer<O, A>): void;

    public get hasSubscribers(): boolean {
        return !fastSetIsEmpty(this._listeners);
    }

    protected firstSubscribed(): void {
        // logInfo(`first subscribed ${this.id}`, this);
        return;
    }

    protected lastUnsubscribed(): void {
        // logInfo(`last unsubscribed ${this.id}`, this);
        return;
    }

    public subscribe(l: ValueListener<O, A>): boolean {
        // addSubscription(this, l);
        listenersAdded++;
        // if (Math.random() < 0.001) {
        //     debugger;
        // }

        const wasEmpty = fastSetIsEmpty(this._listeners);
        this._listeners = fastSetAdd(this._listeners, l);

        if (wasEmpty) {
            this.firstSubscribed();
        }

        return true;
    }

    public unsubscribe(l: ValueListener<O, A>): void {
        listenersRemoved++;

        this._listeners = fastSetDelete(this._listeners, l);

        if (fastSetIsEmpty(this._listeners)) {
            this.lastUnsubscribed();
        }
    }

    public abstract getObjectInspector(
        force: ForceValueProducer<O, A> | undefined
    ): ObjectInspector<O, A> | Loading | undefined;
}

export type MaybeForwardee<O, A> = ValueProducer<O, A> | Loading | undefined;

export abstract class BaseForwardingValueProducer<O, A> extends SubscribableValueProducer<O, A> {
    protected abstract getCurrentForwardee(): MaybeForwardee<O, A>;

    public get current(): PotentialValue<O, A> {
        const forwardee = this.getCurrentForwardee();
        if (!isLoadedDefined(forwardee)) return forwardee;
        return forwardee.current;
    }

    public set(v: Value<O, A>): boolean {
        const forwardee = this.getCurrentForwardee();
        if (!isLoadedDefined(forwardee)) {
            logError(`Trying to set an invalid value`);
            return false;
        }
        return forwardee.set(v);
    }

    public addProperty(name: string, vp: ValueProducer<O, A>): void {
        const forwardee = this.getCurrentForwardee();
        if (!isLoadedDefined(forwardee)) {
            logError(`Trying to add property to an invalid value`);
            return;
        }
        forwardee.addProperty(name, vp);
    }

    public removeProperty(name: string): void {
        const forwardee = this.getCurrentForwardee();
        if (!isLoadedDefined(forwardee)) {
            logError(`Trying to remove property from an invalid value`);
            return;
        }
        forwardee.removeProperty(name);
    }

    public appendItem(vp: ValueProducer<O, A>): void {
        const forwardee = this.getCurrentForwardee();
        if (!isLoadedDefined(forwardee)) {
            logError(`Trying to append an item to an invalid value`);
            return;
        }
        forwardee.appendItem(vp);
    }

    public removeFromParent(): void {
        const forwardee = this.getCurrentForwardee();
        if (forwardee === LOADING || forwardee === undefined) return undefined;
        forwardee.removeFromParent();
    }

    public removeLastItem(): void {
        const forwardee = this.getCurrentForwardee();
        if (!isLoadedDefined(forwardee)) {
            logError(`Trying to remove an item from an invalid value`);
            return;
        }
        forwardee.removeLastItem();
    }

    public removeItemAtIndex(index: number): void {
        const forwardee = this.getCurrentForwardee();
        if (!isLoadedDefined(forwardee)) {
            logError(`Trying to remove an item from an invalid value`);
            return;
        }
        forwardee.removeItemAtIndex(index);
    }

    public removeItem(item: ValueProducer<O, A>): void {
        const forwardee = this.getCurrentForwardee();
        if (forwardee === LOADING || forwardee === undefined) return undefined;
        forwardee.removeItem(item);
    }

    public getObjectInspector(
        force: ForceValueProducer<O, A> | undefined
    ): ObjectInspector<O, A> | Loading | undefined {
        force?.(this);
        const forwardee = this.getCurrentForwardee();
        if (!isLoadedDefined(forwardee)) return forwardee;
        return forwardee.getObjectInspector(force);
    }
}

export abstract class ForwardingValueProducer<O, A>
    extends BaseForwardingValueProducer<O, A>
    implements ValueListener<O, A>
{
    private _currentForwardee: MaybeForwardee<O, A> = LOADING;
    private _isDirty: boolean = true;

    protected abstract recomputeForwardee(currentForwardee: MaybeForwardee<O, A>): MaybeForwardee<O, A>;

    protected firstSubscribed(): void {
        super.firstSubscribed();

        assert(this._currentForwardee === LOADING);
    }

    protected lastUnsubscribed(): void {
        try {
            super.lastUnsubscribed();

            if (isLoadedDefined(this._currentForwardee)) {
                this._currentForwardee.unsubscribe(this);
            }
        } finally {
            // We have to set dirty because
            // we don't get updates anymore and when we get subscribers again
            // it might be out of date.
            this._currentForwardee = LOADING;
            this._isDirty = true;
        }
    }

    public getCurrentForwardee(): MaybeForwardee<O, A> {
        if (!this._isDirty) {
            return this._currentForwardee;
        }

        const { hasSubscribers } = this;

        const newForwardee = this.recomputeForwardee(this._currentForwardee);
        if (newForwardee !== this._currentForwardee) {
            if (isLoadedDefined(this._currentForwardee)) {
                this._currentForwardee.unsubscribe(this);
            }

            // This can happen when we're doing a recursive computation.  If
            // we don't abort here, we can hit a stack overflow.
            if (this.hasSubscribers !== hasSubscribers) {
                return detectedRecursiveComputation();
            }

            if (hasSubscribers) {
                this._currentForwardee = newForwardee;
                if (isLoadedDefined(this._currentForwardee)) {
                    this._currentForwardee.subscribe(this);
                }
            }
        }

        if (this.hasSubscribers) {
            this._isDirty = false;
        }

        return newForwardee;
    }

    protected needsDirtying(sender: ValueProducer<O, A>): boolean {
        return !isLoadedDefined(this._currentForwardee) || sender !== this._currentForwardee;
    }

    public setDirty(sender: ValueProducer<O, A>): void {
        if (this._isDirty) return;

        if (this.needsDirtying(sender)) {
            this._isDirty = true;
        }

        this.setListenersDirty();
    }
}

export class SettableForwardingValueProducer<O, A> extends ForwardingValueProducer<O, A> {
    private _forwardee: ValueProducer<O, A> | undefined;

    public get forwardee(): ValueProducer<O, A> | undefined {
        return this._forwardee;
    }

    protected recomputeForwardee(): ValueProducer<O, A> | Loading | undefined {
        if (this._forwardee === undefined) return LOADING;
        return this._forwardee;
    }

    public setForwardee(vp: ValueProducer<O, A>): void {
        assert(this._forwardee === undefined);
        this._forwardee = vp;
        this.setDirty(this);
    }
}

export abstract class ImmutableSubscribableValueProducer<O, A> extends SubscribableValueProducer<O, A> {
    public set(_v: Value<O, A>): boolean {
        return panic("Cannot set an ImmutableSubscribableValueProducer");
    }

    public addProperty(_name: string, _vp: ValueProducer<O, A>): void {
        return panic("Cannot add property to an ImmutableSubscribableValueProducer");
    }

    public removeProperty(_name: string): void {
        return panic("Cannot remove property from an ImmutableSubscribableValueProducer");
    }

    public appendItem(_vp: ValueProducer<O, A>): void {
        return panic("Cannot add item to an ImmutableSubscribableValueProducer");
    }

    public removeFromParent(): void {
        return panic("Cannot remove an ImmutableSubscribableValueProducer from its parent");
    }

    public removeLastItem(): void {
        return panic("Cannot remove an item from an ImmutableSubscribableValueProducer");
    }

    public removeItemAtIndex(_index: number): void {
        return panic("Cannot remove an item from an ImmutableSubscribableValueProducer");
    }

    public removeItem(_item: ValueProducer<O, A>): void {
        return panic("Cannot remove an item from an ImmutableSubscribableValueProducer");
    }
}

export interface SubscribeValueProducer<O, A> {
    (vp: ValueProducer<O, A>): void;
}

export interface SubscriptionClient<O, A, T> extends ValueListener<O, A> {
    recompute(force: ForceValueProducer<O, A>): T;
}

function forceWithoutSubscribe<O, A>(vp: ValueProducer<O, A>): PotentialValue<O, A> {
    return vp.current;
}

// FIXME: Maybe this shouldn't be a separate class, but instead be a mixin?
export class SubscriptionManager<O, A, T> {
    private _subscribedToValueProducers: FastSet<ValueProducer<O, A>>;
    private _newValueProducers: FastSet<ValueProducer<O, A>>;
    // private _running: boolean = false;

    constructor(private readonly _subscriber: SubscriptionClient<O, A, T>) {}

    private forceAndSubscribe = (vp: ValueProducer<O, A>): PotentialValue<O, A> => {
        // assert(this._running);
        if (!fastSetHas(this._subscribedToValueProducers, vp)) {
            if (!vp.subscribe(this._subscriber)) {
                return vp.current;
            }
        }
        this._newValueProducers = fastSetAdd(this._newValueProducers, vp);

        return vp.current;
    };

    public run(needSubscribe: boolean): T | undefined {
        const oldVPs = this._subscribedToValueProducers;
        if (!needSubscribe) {
            assert(fastSetIsEmpty(oldVPs));
        } else {
            if (oldVPs instanceof Set) {
                for (const vp of oldVPs) {
                    if (vp instanceof SubscribableValueProducer) {
                        assert(vp.hasSubscribers);
                    }
                }
            } else if (oldVPs !== undefined) {
                if (oldVPs instanceof SubscribableValueProducer) {
                    assert(oldVPs.hasSubscribers);
                }
            }
        }

        // assert(!this._running);
        if (!fastSetIsEmpty(this._newValueProducers)) {
            return detectedRecursiveComputation();
        }

        let result: T;

        try {
            // this._running = true;
            if (needSubscribe) {
                result = this._subscriber.recompute(this.forceAndSubscribe);
            } else {
                result = this._subscriber.recompute(forceWithoutSubscribe);
            }
        } finally {
            // assert(this._running);
            // this._running = false;

            if (needSubscribe) {
                if (oldVPs instanceof Set) {
                    for (const vp of oldVPs) {
                        if (!fastSetHas(this._newValueProducers, vp)) {
                            vp.unsubscribe(this._subscriber);
                        }
                    }
                } else if (oldVPs !== undefined) {
                    if (!fastSetHas(this._newValueProducers, oldVPs)) {
                        oldVPs.unsubscribe(this._subscriber);
                    }
                }

                const old = this._subscribedToValueProducers;
                this._subscribedToValueProducers = this._newValueProducers;
                this._newValueProducers = fastSetClear(old);
            }
        }

        return result;
    }

    public unsubscribe(): void {
        if (this._subscribedToValueProducers instanceof Set) {
            for (const vp of this._subscribedToValueProducers) {
                vp.unsubscribe(this._subscriber);
            }
        } else if (this._subscribedToValueProducers !== undefined) {
            this._subscribedToValueProducers.unsubscribe(this._subscriber);
        }
        this._subscribedToValueProducers = fastSetClear(this._subscribedToValueProducers);
    }
}

export abstract class TwoPhaseRecomputingValueProducer<O, A>
    extends ImmutableSubscribableValueProducer<O, A>
    implements SubscriptionClient<O, A, PotentialValue<O, A>>
{
    private _isDirty: boolean = true;

    // Must not be `undefined` if `_isDirty` is `false`
    private _currentValue: PotentialValue<O, A>;
    // `false` means the value producers need recomputing.  Must not be `false`
    // if `_isDirty` is `false`.
    private _valueProducers: readonly ValueProducer<O, A>[] | Loading | undefined | false = false;

    private readonly _subscriptionManager = new SubscriptionManager(this);

    protected abstract recomputeValueProducers(
        force: ForceValueProducer<O, A>
    ): readonly ValueProducer<O, A>[] | Loading | undefined;
    protected abstract recomputeFromValueProducers(vps: readonly ValueProducer<O, A>[]): PotentialValue<O, A>;

    public recompute(force: ForceValueProducer<O, A>): PotentialValue<O, A> {
        const vps = this.recomputeValueProducers(force);
        if (!isLoadedDefined(vps)) return vps;

        vps.forEach(force);
        return vps as ValueProducer<O, A>[];
    }

    protected lastUnsubscribed(): void {
        super.lastUnsubscribed();

        // We lose updates when we're not subscribed to our dependencies,
        // so we need to recompute when we subscribe again.
        this._isDirty = true;
        this._currentValue = undefined;
        this._valueProducers = false;
        this._subscriptionManager.unsubscribe();
    }

    public get current(): PotentialValue<O, A> {
        if (!this._isDirty) {
            return this._currentValue;
        }

        const { hasSubscribers } = this;
        let valueProducers: readonly ValueProducer<O, A>[] | Loading | undefined;

        if (this._valueProducers === false) {
            const maybeValueProducers = this._subscriptionManager.run(hasSubscribers);
            if (this.hasSubscribers !== hasSubscribers) {
                return detectedRecursiveComputation();
            }
            if (isLoadedDefined(maybeValueProducers) && !isArray(maybeValueProducers)) {
                return panic("We computed an incorrect type value producers");
            }
            valueProducers = maybeValueProducers;
        } else {
            valueProducers = this._valueProducers;
        }

        let result: PotentialValue<O, A>;
        if (!isLoadedDefined(valueProducers)) {
            result = valueProducers;
        } else {
            result = this.recomputeFromValueProducers(valueProducers);
        }

        if (hasSubscribers) {
            this._currentValue = result;
            this._isDirty = false;
        }

        return result;
    }

    protected setValuesDirty(): void {
        if (this._isDirty) return;

        this._isDirty = true;
        this.setListenersDirty();
    }

    protected setDependenciesDirty(): void {
        const wasDirty = this._isDirty;
        if (wasDirty && this._valueProducers === false) return;

        this._isDirty = true;
        this._valueProducers = false;

        if (!wasDirty) {
            this.setListenersDirty();
        }
    }

    public abstract setDirty(sender: ValueProducer<O, A>): void;

    public getObjectInspector(
        force: ForceValueProducer<O, A> | undefined
    ): ObjectInspector<O, A> | Loading | undefined {
        return makeObjectInspector(this, force);
    }
}

export abstract class RecomputingValueProducer<O, A>
    extends ImmutableSubscribableValueProducer<O, A>
    implements SubscriptionClient<O, A, PotentialValue<O, A>>
{
    protected currentValue: PotentialValue<O, A>;
    private _isDirty: boolean = true;
    private readonly _subscriptionManager = new SubscriptionManager(this);

    public abstract recompute(force: ForceValueProducer<O, A>): PotentialValue<O, A>;

    protected lastUnsubscribed(): void {
        super.lastUnsubscribed();

        // We lose updates when we're not subscribed to our dependencies,
        // so we need to recompute when we subscribe again.
        this.currentValue = undefined;
        this._isDirty = true;
        this._subscriptionManager.unsubscribe();
    }

    public get current(): PotentialValue<O, A> {
        let v: PotentialValue<O, A>;
        if (this._isDirty) {
            const { hasSubscribers } = this;
            v = this._subscriptionManager.run(hasSubscribers);
            if (this.hasSubscribers !== hasSubscribers) {
                return detectedRecursiveComputation();
            }
            if (hasSubscribers) {
                this.currentValue = v;
                this._isDirty = false;
            }
        } else {
            v = this.currentValue;
        }
        return v;
    }

    public setDirty(): void {
        if (this._isDirty) return;

        this._isDirty = true;
        this.setListenersDirty();
    }

    public getObjectInspector(
        force: ForceValueProducer<O, A> | undefined
    ): ObjectInspector<O, A> | Loading | undefined {
        return makeObjectInspector(this, force);
    }
}

export abstract class RecomputingFromContainerValueProducer<O, A> extends RecomputingValueProducer<O, A> {
    constructor(_appEnvironment: A | undefined, protected readonly _container: ValueProducer<O, A>) {
        super();
    }

    protected abstract recomputeFromContainer(
        container: Value<O, A>,
        force: ForceValueProducer<O, A>
    ): PotentialValue<O, A>;

    public recompute(force: ForceValueProducer<O, A>): PotentialValue<O, A> {
        const container = force(this._container);
        if (!isLoadedDefined(container)) return container;

        return this.recomputeFromContainer(container, force);
    }
}

export abstract class RecomputingFromListValueProducer<O, A> extends RecomputingFromContainerValueProducer<O, A> {
    protected abstract recomputeFromList(
        list: ReadonlyArray<ValueProducer<O, A>>,
        force: ForceValueProducer<O, A>
    ): PotentialValue<O, A>;

    protected recomputeFromContainer(container: Value<O, A>, force: ForceValueProducer<O, A>): PotentialValue<O, A> {
        return this.recomputeFromList(asArrayValue(container), force);
    }
}

export abstract class RecomputingFromObjectValueProducer<O, A> extends RecomputingValueProducer<O, A> {
    constructor(_appEnvironment: A | undefined, protected readonly _container: ValueProducer<O, A>) {
        super();
    }

    protected abstract recomputeFromObject(
        inpector: ObjectInspector<O, A>,
        force: ForceValueProducer<O, A>
    ): PotentialValue<O, A>;

    public recompute(force: ForceValueProducer<O, A>): PotentialValue<O, A> {
        const inspector = this._container.getObjectInspector(force);
        if (!isLoadedDefined(inspector)) return inspector;

        return this.recomputeFromObject(inspector, force);
    }
}

export abstract class RecomputingFromOneValueProducer<O, A> extends RecomputingValueProducer<O, A> {
    constructor(_appEnvironment: A, private readonly _input: ValueProducer<O, A>) {
        super();
    }

    protected abstract recomputeFromInput(input: Value<O, A>, force: ForceValueProducer<O, A>): PotentialValue<O, A>;

    public recompute(force: ForceValueProducer<O, A>): PotentialValue<O, A> {
        const input = force(this._input);
        if (input === LOADING) return LOADING;
        return this.recomputeFromInput(input, force);
    }
}

export abstract class RecomputingFromTwoValueProducer<O, A> extends TwoPhaseRecomputingValueProducer<O, A> {
    constructor(
        _appEnvironment: A,
        private readonly _first: ValueProducer<O, A>,
        private readonly _second: ValueProducer<O, A>
    ) {
        super();
    }

    protected abstract recomputeFromInputs(first: Value<O, A>, second: Value<O, A>): PotentialValue<O, A>;

    protected recomputeValueProducers(): readonly ValueProducer<O, A>[] | Loading | undefined {
        return [this._first, this._second];
    }

    protected recomputeFromValueProducers([firstVP, secondVP]: readonly ValueProducer<O, A>[]): PotentialValue<O, A> {
        const first = firstVP.current;
        const second = secondVP.current;
        if (first === LOADING || second === LOADING) return LOADING;
        return this.recomputeFromInputs(first, second);
    }

    public setDirty(): void {
        this.setValuesDirty();
    }
}

export abstract class RecomputingFromThreeValueProducer<O, A> extends TwoPhaseRecomputingValueProducer<O, A> {
    constructor(
        _appEnvironment: A,
        private readonly _first: ValueProducer<O, A>,
        private readonly _second: ValueProducer<O, A>,
        private readonly _third: ValueProducer<O, A>
    ) {
        super();
    }

    protected abstract recomputeFromInputs(
        first: Value<O, A>,
        second: Value<O, A>,
        third: Value<O, A>
    ): PotentialValue<O, A>;

    protected recomputeValueProducers(): readonly ValueProducer<O, A>[] | Loading | undefined {
        return [this._first, this._second, this._third];
    }

    protected recomputeFromValueProducers([firstVP, secondVP, thirdVP]: readonly ValueProducer<O, A>[]): PotentialValue<
        O,
        A
    > {
        const first = firstVP.current;
        const second = secondVP.current;
        const third = thirdVP.current;
        if (first === LOADING || second === LOADING || third === LOADING) return LOADING;
        return this.recomputeFromInputs(first, second, third);
    }

    public setDirty(): void {
        this.setValuesDirty();
    }
}

export abstract class RecomputingFromThreeOrFourValueProducer<O, A> extends TwoPhaseRecomputingValueProducer<O, A> {
    constructor(
        _appEnvironment: A,
        private readonly _first: ValueProducer<O, A>,
        private readonly _second: ValueProducer<O, A>,
        private readonly _third: ValueProducer<O, A>,
        private readonly _fourth: ValueProducer<O, A> | undefined
    ) {
        super();
    }

    protected abstract recomputeFromInputs(
        first: Value<O, A>,
        second: Value<O, A>,
        third: Value<O, A>,
        fourth: PotentialValue<O, A>
    ): PotentialValue<O, A>;

    protected recomputeValueProducers(): readonly ValueProducer<O, A>[] | Loading | undefined {
        const vps = [this._first, this._second, this._third];
        if (this._fourth !== undefined) {
            vps.push(this._fourth);
        }
        return vps;
    }

    protected recomputeFromValueProducers([firstVP, secondVP, thirdVP, fourthVP]: readonly ValueProducer<
        O,
        A
    >[]): PotentialValue<O, A> {
        const first = firstVP.current;
        const second = secondVP.current;
        const third = thirdVP.current;
        const fourth = definedMap(fourthVP, vp => vp.current);
        if (first === LOADING || second === LOADING || third === LOADING) return LOADING;
        return this.recomputeFromInputs(first, second, third, fourth);
    }

    public setDirty(): void {
        this.setValuesDirty();
    }
}

export abstract class RecomputingFromTwoWithDependenciesValueProducer<O, A> extends RecomputingValueProducer<O, A> {
    constructor(
        _appEnvironment: A,
        protected readonly _first: ValueProducer<O, A>,
        protected readonly _second: ValueProducer<O, A>
    ) {
        super();
    }

    protected abstract recomputeFromInputs(
        first: Value<O, A>,
        second: Value<O, A>,
        force: ForceValueProducer<O, A>
    ): PotentialValue<O, A>;

    public recompute(force: ForceValueProducer<O, A>): PotentialValue<O, A> {
        const first = force(this._first);
        const second = force(this._second);
        if (first === LOADING || second === LOADING) return LOADING;
        return this.recomputeFromInputs(first, second, force);
    }
}

export abstract class RecomputingFromOneOrTwoValueProducer<O, A> extends RecomputingValueProducer<O, A> {
    constructor(
        _appEnvironment: A,
        private readonly _first: ValueProducer<O, A>,
        private readonly _second: ValueProducer<O, A> | undefined
    ) {
        super();
    }

    protected abstract recomputeFromInputs(
        first: Value<O, A>,
        second: Value<O, A>,
        force: ForceValueProducer<O, A>
    ): PotentialValue<O, A>;

    public recompute(force: ForceValueProducer<O, A>): PotentialValue<O, A> {
        const first = force(this._first);
        const second = definedMap(this._second, force);
        if (first === LOADING || second === LOADING) return LOADING;
        return this.recomputeFromInputs(first, second, force);
    }
}

export abstract class ForwardingFromTwoValueProducer<O, A> extends ForwardingValueProducer<O, A> {
    constructor(
        _appEnvironment: A,
        protected readonly first: ValueProducer<O, A>,
        protected readonly second: ValueProducer<O, A>
    ) {
        super();
    }

    protected firstSubscribed(): void {
        super.firstSubscribed();
        this.first.subscribe(this);
        this.second.subscribe(this);
    }

    protected lastUnsubscribed(): void {
        super.lastUnsubscribed();
        this.first.unsubscribe(this);
        this.second.unsubscribe(this);
    }

    protected abstract recomputeForwardeeFromInputs(
        first: PotentialValue<O, A>,
        second: PotentialValue<O, A>,
        currentForwardee: MaybeForwardee<O, A>
    ): ValueProducer<O, A> | Loading | undefined;

    protected recomputeForwardee(currentForwardee: MaybeForwardee<O, A>): ValueProducer<O, A> | Loading | undefined {
        return this.recomputeForwardeeFromInputs(this.first.current, this.second.current, currentForwardee);
    }
}

export abstract class ForwardingFromOneOrTwoValueProducer<O, A> extends ForwardingValueProducer<O, A> {
    constructor(
        _appEnvironment: A,
        private readonly _first: ValueProducer<O, A>,
        private readonly _second: ValueProducer<O, A> | undefined
    ) {
        super();
    }

    protected firstSubscribed(): void {
        super.firstSubscribed();
        this._first.subscribe(this);
        if (this._second !== undefined) {
            this._second.subscribe(this);
        }
    }

    protected lastUnsubscribed(): void {
        super.lastUnsubscribed();
        this._first.unsubscribe(this);
        if (this._second !== undefined) {
            this._second.unsubscribe(this);
        }
    }

    protected abstract recomputeForwardeeFromInputs(
        first: PotentialValue<O, A>,
        second: PotentialValue<O, A>,
        currentForwardee: MaybeForwardee<O, A>
    ): ValueProducer<O, A> | Loading | undefined;

    protected recomputeForwardee(currentForwardee: MaybeForwardee<O, A>): ValueProducer<O, A> | Loading | undefined {
        return this.recomputeForwardeeFromInputs(
            this._first.current,
            definedMap(this._second, vp => vp.current),
            currentForwardee
        );
    }
}

enum State {
    Dirty,
    Recomputing,
    UpToDate,
    Again,
}

//
//                            +-------+
//        +-------------------+ again +-------+
//        |   done w/o subs   +---+---+       |
//        |                       ^           | done
//        |                       | dirty     | with
//        |                       |           | subs
//   +----+--+             +------+------+    |
//   | dirty +------------>+ recomputing +<---+
//   +-+---+-+    force    +--+---+------+
//     ^   ^                  |   |
//     |   +------------------+   | done
//     |      done w/o subs       | with
//     |                          | subs
//     |                          v
//     |                    +-----+------+
//     +--------------------+ up to date |
//         dirty or unsub   +------------+

export abstract class AsyncRecomputingValueProducer<O, A>
    extends ImmutableSubscribableValueProducer<O, A>
    implements ValueListener<O, A>
{
    protected currentValue: PotentialValue<O, A> = LOADING;
    private _state: State = State.Dirty;
    private _omitSetListenersDirty: boolean = false;

    protected abstract recomputeAsync(): PotentialValue<O, A> | Promise<PotentialValue<O, A>>;

    protected lastUnsubscribed(): void {
        super.lastUnsubscribed();

        // We lose updates when we're not subscribed to our dependencies,
        // so we need to recompute when we subscribe again.
        this._state = State.Dirty;
    }

    private async runRecompute(): Promise<void> {
        let didChange = false;

        for (;;) {
            // TS forces us into this cast because it doesn't realize
            // that the value of `this._state` can change between here
            // and the end of this loop.
            assert((this._state === State.Recomputing) as any);

            let newCurrent: PotentialValue<O, A>;
            try {
                const resultOrPromise = this.recomputeAsync();
                if (resultOrPromise instanceof Promise) {
                    newCurrent = await resultOrPromise;
                } else {
                    newCurrent = resultOrPromise;
                }
            } catch {
                newCurrent = LOADING;
            }

            if (!this.hasSubscribers) {
                this._state = State.Dirty;
                return;
            }

            if (newCurrent !== this.currentValue) {
                this.currentValue = newCurrent;
                didChange = true;
            }

            if (this._state === State.Again) {
                this._state = State.Recomputing;
                continue;
            }

            break;
        }

        this._state = State.UpToDate;
        if (didChange && !this._omitSetListenersDirty) {
            this.setListenersDirty();
        }
    }

    private runRecomputeIfNecessary(): void {
        if (this.hasSubscribers && this._state === State.Dirty) {
            this._state = State.Recomputing;
            // eslint-disable-next-line @typescript-eslint/no-floating-promises
            this.runRecompute();
        }
    }

    public get current(): PotentialValue<O, A> {
        this._omitSetListenersDirty = true;
        this.runRecomputeIfNecessary();
        this._omitSetListenersDirty = false;
        return this.currentValue;
    }

    public setDirty(): void {
        if (this._state === State.UpToDate) {
            this._state = State.Dirty;
            this.setListenersDirty();
        } else if (this._state === State.Recomputing) {
            this._state = State.Again;
        }
    }
}

export abstract class FetchingValueProducer<O, A, T> extends AsyncRecomputingValueProducer<O, A> {
    constructor(private readonly _promiseCache: Map<T, Promise<Value<O, A>>>, private readonly _input: T) {
        super();
    }

    protected recomputeAsync(): Value<O, A> | Promise<Value<O, A>> {
        const promise = this._promiseCache.get(this._input);
        if (promise !== undefined) {
            return promise;
        }

        const resultOrPromise = this.fetch(this._input);
        if (resultOrPromise instanceof Promise) {
            this._promiseCache.set(this._input, resultOrPromise);
        }
        return resultOrPromise;
    }

    protected abstract fetch(input: T): Value<O, A> | Promise<Value<O, A>>;
}

export function asMaybeNumber<O, A>(v: Value<O, A>): number | undefined {
    if (v === undefined) return undefined;
    if (typeof v === "number") return v;
    if (typeof v === "boolean") return v ? 1 : 0;
    if (typeof v === "string") {
        const n = parseNumber(v);
        if (n !== undefined) return n;
    }
    return undefined;
}

export function asNumber<O, A>(v: Value<O, A>): number {
    return withDefault(asMaybeNumber(v), 0);
}

export function asMaybeBoolean<O, A>(v: Value<O, A>): boolean | undefined {
    if (typeof v === "boolean") return v;
    if (typeof v === "number") return v !== 0;
    if (typeof v === "string") {
        if (["true", "yes", "y", "1", "x"].indexOf(v.toLowerCase()) >= 0) return true;
        if (["false", "no", "n", "0", "-"].indexOf(v.toLowerCase()) >= 0) return false;
    }
    return undefined;
}

export function asBoolean<O, A>(v: Value<O, A>): boolean {
    return asMaybeBoolean(v) === true;
}

export function asMaybeString<O, A>(x: Value<O, A>): string | undefined {
    if (typeof x === "string") return x;
    if (typeof x === "number") return formatNumber(x);
    if (typeof x === "boolean") return x.toString();
    if (x instanceof Date) return formatDateTime(x, DateFormat.Short, TimeFormat.WithSeconds);
    if (x instanceof GlideDateTime) return x.asString();
    return undefined;
}

export function asEnum<T>(v: Value<any, any>): T {
    return asString(v) as unknown as T;
}

export function asString<O, A>(x: Value<O, A>): string {
    return withDefault(asMaybeString(x), "");
}

export function asBasePrimitiveValue<O, A>(x: Value<O, A>): BasePrimitiveValue {
    return isBasePrimitiveValue(x) ? x : asString(x);
}

export function asDate<O, A>(v: Value<O, A>): Date | undefined {
    if (v instanceof Date) return v;
    if (v instanceof GlideDateTime) return v.asTimeZoneAgnosticDate();
    if (typeof v === "number") return new Date(v);
    if (typeof v === "string") {
        const d = makeDateFromString(v, true);
        if (d !== undefined) {
            return convertDateToTimeZoneAgnostic(d);
        }
    }
    return undefined;
}

export function parseValueAsDateSync<O, A>(v: Value<O, A>): Date | undefined {
    if (typeof v === "string") {
        return parseUserDateTimeZoneAgnosticSync(v, false);
    }
    return asDate(v);
}

export function parseValueAsDate<O, A>(v: Value<O, A>): Promise<Date | undefined> | Date | undefined {
    if (typeof v === "string") {
        return parseUserDateTimeZoneAgnostic(v, false);
    }
    return asDate(v);
}

export function asCallable<O, A>(v: Value<O, A> | Evaluable<O, A>): Callable<O, A> {
    if (!(v instanceof Callable)) {
        return panic("Tried to call an uncallable object");
    }
    return v;
}

export function asArrayValue<O, A>(v: Value<O, A>): ValueProducer<O, A>[] {
    if (Array.isArray(v)) return v;
    return [];
}

export function asObjectValue<O, A>(v: PotentialValue<O, A>): ObjectValue<O, A> {
    if (!isObjectValue(v)) {
        return {};
    }
    return v;
}

export function isValueNotEmpty<O, A>(v: PotentialValue<O, A>): boolean | Loading {
    if (v === LOADING) return LOADING;
    if (v === undefined) return false;
    if (v === "") return false;
    if (typeof v === "number" && isNaN(v)) return false;
    if (isArray(v)) return v.length > 0;
    return true;
}

export function isListSearched<O, A>(
    displayedList: PotentialValue<O, A>,
    unfilteredList: PotentialValue<O, A>
): boolean {
    if (!isLoadedDefined(unfilteredList) || !isLoadedDefined(displayedList)) return false;

    const displayedLength = asArrayValue(displayedList).length;
    const unfilteredLength = asArrayValue(unfilteredList).length;

    return displayedLength !== unfilteredLength;
}

export function evaluateStringArray<O, A>(
    columnNamesEvaluable: EnvironmentBoundEvaluable<O, A> | undefined,
    ctx: ValueProducer<O, A>,
    actionSource: ActionSource | undefined
): Set<string> | Loading | undefined {
    const columnNames = columnNamesEvaluable?.eval(ctx, actionSource)?.current;
    if (!isLoadedDefined(columnNames)) return columnNames;

    return new Set(asArrayValue(columnNames).map(vp => asString(vp.current)));
}

export function deconstructLatLong<O, A>(
    vp: ValueProducer<O, A>,
    force: ForceValueProducer<O, A>
): { latitude: number; longitude: number } | Loading | undefined {
    const inspector = vp.getObjectInspector(force);
    if (!isLoadedDefined(inspector)) return inspector;

    const latitude = loadedDefinedMap(definedMap(inspector.getProperty("latitude"), force), asNumber);
    const longitude = loadedDefinedMap(definedMap(inspector.getProperty("longitude"), force), asNumber);
    if (latitude === undefined || longitude === undefined) return undefined;

    return { latitude, longitude };
}

export class ValueAwaiter<O, A> implements ValueListener<O, A> {
    private readonly _valueResolvers: ((v: Value<O, A>) => void)[] = [];
    private readonly _inspectorResolvers: ((oi: ObjectInspector<O, A> | undefined) => void)[] = [];

    constructor(private readonly _vp: ValueProducer<O, A>) {
        _vp.subscribe(this);
    }

    public setDirty(sender: ValueProducer<O, A>): void {
        assert(sender === this._vp);

        if (this._valueResolvers.length > 0) {
            const { current } = this._vp;
            if (current !== LOADING) {
                for (;;) {
                    const r = this._valueResolvers.shift();
                    if (r === undefined) break;

                    r(current);
                }
            }
        }

        if (this._inspectorResolvers.length > 0) {
            const inspector = this._vp.getObjectInspector(undefined);
            if (inspector !== LOADING) {
                for (;;) {
                    const r = this._inspectorResolvers.shift();
                    if (r === undefined) break;

                    r(inspector);
                }
            }
        }
    }

    public retire(): void {
        this._vp.unsubscribe(this);
    }

    public getValue(): PotentialValue<O, A> {
        return this._vp.current;
    }

    public getObjectInspector(): ObjectInspector<O, A> | Loading | undefined {
        return this._vp.getObjectInspector(undefined);
    }

    public async waitForValue(): Promise<Value<O, A>> {
        const { current } = this._vp;
        if (current !== LOADING) return current;

        return new Promise(resolve => this._valueResolvers.push(resolve));
    }

    public async waitForObjectInspector(): Promise<ObjectInspector<O, A> | undefined> {
        const inspector = this._vp.getObjectInspector(undefined);
        if (inspector !== LOADING) return inspector;

        return new Promise(resolve => this._inspectorResolvers.push(resolve));
    }
}

export async function waitForValueFromValueProducer<O, A>(vp: ValueProducer<O, A>): Promise<Value<O, A>> {
    const awaiter = new ValueAwaiter(vp);
    const result = await awaiter.waitForValue();
    awaiter.retire();
    return result;
}

export async function waitForObjectInspectorFromValueProducer<O, A>(
    vp: ValueProducer<O, A>
): Promise<ObjectInspector<O, A> | undefined> {
    const awaiter = new ValueAwaiter(vp);
    const result = await awaiter.waitForObjectInspector();
    awaiter.retire();
    return result;
}

export function getValueFromValueProducer<O, A>(vp: ValueProducer<O, A>): PotentialValue<O, A> {
    const awaiter = new ValueAwaiter(vp);
    const result = awaiter.getValue();
    awaiter.retire();
    return result;
}

export function getObjectInspectorFromValueProducer<O, A>(
    vp: ValueProducer<O, A>
): ObjectInspector<O, A> | Loading | undefined {
    const awaiter = new ValueAwaiter(vp);
    const result = awaiter.getObjectInspector();
    awaiter.retire();
    return result;
}

class SimpleObjectInspector<O, A> implements ObjectInspector<O, A> {
    constructor(private readonly _obj: ObjectValue<O, A>) {}

    public getProperty(name: string): ValueProducer<O, A> | undefined {
        return this._obj[name];
    }

    public getObjectValue(): ObjectValue<O, A> {
        return this._obj;
    }
}

export function makeObjectInspector<O, A>(
    vp: ValueProducer<O, A>,
    force: ForceValueProducer<O, A> | undefined
): ObjectInspector<O, A> | Loading | undefined {
    const v = force !== undefined ? force(vp) : vp.current;
    if (!isLoadedDefined(v)) return v;
    if (!isObjectValue(v)) return undefined;
    return new SimpleObjectInspector(v);
}

export function makeWebhookValue(x: unknown): WebhookValue | undefined {
    if (typeof x === "string") {
        return { type: "string", value: x };
    } else if (typeof x === "number") {
        return { type: "number", value: x };
    } else if (typeof x === "boolean") {
        return { type: "boolean", value: x };
    } else if (x instanceof Date || x instanceof GlideDateTime) {
        return { type: "date-time", value: defined(asDate(x)).toISOString() };
    }
    return undefined;
}
